import { useCallback } from 'react';

import { AxiosResponse } from 'axios';
import { useInfiniteQuery, UseInfiniteQueryResult } from 'react-query';
import { PaginatedAthleteAttendanceReportList, UrlParams } from 'types';
import { client } from 'utils/api-client';
import { encodeUrlParams, urlParamsFromUrl } from 'utils/functions';

interface IUseAthleteAttendance {
  dateStart?: string;
  dateEnd?: string;
  athleteStatuses?: string[];
  events?: string[];
  ordering?: string;
  limit?: string;
  enabled?: boolean;
  csvExport?: string;
}

interface QueryKeyType {
  pageParam?: UrlParams;
  queryKey?: (string | undefined | string[])[];
}

function useAthleteAttendance({
  ordering,
  dateStart,
  dateEnd,
  athleteStatuses,
  events,
  limit = '20',
  enabled = true,
  csvExport,
}: IUseAthleteAttendance): UseInfiniteQueryResult<AxiosResponse<PaginatedAthleteAttendanceReportList>> {
  const fetchAttendanceAttendance = useCallback(
    ({
      pageParam = { limit, offset: '0' },
    }: QueryKeyType): Promise<AxiosResponse<PaginatedAthleteAttendanceReportList>> => {
      const params = pageParam;

      if (dateStart !== undefined) {
        params.start_date = dateStart;
      }
      if (dateEnd !== undefined) {
        params.end_date = dateEnd;
      }
      if (athleteStatuses) {
        params.athlete_statuses = athleteStatuses.join(',');
      }
      if (events && events.length > 0) {
        params.events = events.join(',');
      }
      if (ordering) {
        params.ordering = ordering.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`).concat(',pk');
      }

      return client.get(`reports/athlete-attendance?${encodeUrlParams(params)}`);
    },
    [ordering, dateStart, dateEnd, athleteStatuses, events, limit],
  );

  return useInfiniteQuery(
    ['athlete-attendance', dateStart, dateEnd, athleteStatuses, events, ordering, csvExport],
    fetchAttendanceAttendance,
    {
      getNextPageParam: page => {
        return page.data.next ? urlParamsFromUrl(page.data.next) : undefined;
      },
      enabled:
        enabled &&
        ((dateStart !== undefined && dateEnd !== undefined) ||
          (dateStart === undefined && dateEnd === undefined)),
      staleTime: 86400000, // 1 day
    },
  );
}

export default useAthleteAttendance;
